import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileAlt } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import { notify } from '../../components/Utils/Notifications';
import { formaterJour } from './formatAvances';

/**
 * Destination « Attestations » de l'espace collaborateur (Yros/Kavero#122).
 *
 * Promue en destination du menu (quatrième revirement du 2026-09-25) : elle
 * vivait auparavant en carte sur la page de profil, qui lui passait
 * `statutEmploi` en prop. Devenue une route à part entière (patron de
 * `MesNotesFrais.js`), elle résout ce statut elle-même via le contrat C6
 * (`GET /api/rh/moi`) — même appel que `MesInformations.js`, dupliqué plutôt
 * que remonté en commun : les deux écrans n'ont plus d'autre donnée à
 * partager.
 *
 * Plus aucun téléchargement ici : la RH fait signer le document puis le
 * remet en main propre. L'écran ne dit plus que le statut — « en attente »
 * ou « disponible », auquel cas un message dit d'aller le récupérer. Tenté
 * en sens inverse le 2026-09-28 (téléchargement direct côté salarié),
 * **revenu en arrière le jour même** : l'espace collaborateur ne doit pas
 * pouvoir télécharger ce document, seule la RH le peut (Supervision RH).
 *
 * Un seul bouton de dépôt : `statutEmploi` dit s'il demande une attestation
 * de travail ou de stage — jamais les deux, et rien du tout si le statut est
 * inconnu (aucun contrat).
 */

// `remise` en indigo, jamais l'accueil jaune de l'attente (feedback du
// 2026-09-25) : une attestation remise est un état FINAL et positif, pas
// une décision qui traîne — le confondre visuellement avec « en attente »
// laissait croire au salarié que quelque chose restait à faire. Même teinte
// indigo que l'icône d'en-tête de cette carte, pour rester dans la même
// famille de couleurs que l'écran de Supervision RH.
const COULEUR_ETAT = {
  validee: 'bg-green-100 text-green-800',
  refusee: 'bg-red-100 text-red-800',
  remise: 'bg-indigo-100 text-indigo-800',
};
const COULEUR_ETAT_DEFAUT = 'bg-yellow-100 text-yellow-800';

const PAR_PAGE = 5;

function MesAttestations() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [statutEmploi, setStatutEmploi] = useState(null);
  const [chargementStatut, setChargementStatut] = useState(true);
  const [demandes, setDemandes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const reponse = await apiService.getElement('/rh/moi');
        if (!vivant) return;
        const donnees = reponse?.data;
        setStatutEmploi(donnees?.ok ? donnees.donnees?.statut_emploi || null : null);
      } catch (e) {
        signalerErreur(e, 'MesAttestations.chargerStatutEmploi');
        if (vivant) {
          setStatutEmploi(null);
          notify(t('mesAttestations.statutError'), '', 3);
        }
      } finally {
        if (vivant) setChargementStatut(false);
      }
    })();
    return () => { vivant = false; };
  }, []);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const reponse = await apiService.listerDemandesRh();
      setDemandes((reponse?.demandes || []).filter(
        (demande) => demande.type === 'attestation'));
    } catch (e) {
      setDemandes([]);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (statutEmploi) charger();
  }, [statutEmploi, charger]);

  const demander = useCallback(async () => {
    setEnCours(true);
    setErreur(false);
    try {
      const resultat = await apiService.creerDemandeRh({
        type: 'attestation',
        contenu: { type_attestation: statutEmploi },
      });
      if (!resultat?.ok) {
        setErreur(true);
      } else {
        await charger();
      }
    } catch (e) {
      signalerErreur(e, 'MesAttestations.demander');
      setErreur(true);
    } finally {
      setEnCours(false);
    }
  }, [statutEmploi, charger]);

  // La plus récente d'abord : c'est celle qui intéresse le salarié juste
  // après un dépôt, pas la plus ancienne. `date_demande` seul suffit — deux
  // demandes du même salarié n'arrivent jamais à la même seconde.
  const demandesTriees = useMemo(
    () => [...demandes].sort(
      (a, b) => (b.date_demande || '').localeCompare(a.date_demande || '')),
    [demandes]
  );
  // Une seule à la fois : valider deux fois la même chose n'apporte rien à
  // la RH, et l'écran ne propose qu'un type — la reproposer pendant qu'une
  // copie attend déjà sa décision ne ferait qu'empiler des doublons.
  const dejaEnAttente = demandes.some((demande) => demande.etat === 'en_attente');
  const pages = Math.max(1, Math.ceil(demandesTriees.length / PAR_PAGE));
  const pageCourante = Math.min(page, pages);
  const affichees = demandesTriees.slice(
    (pageCourante - 1) * PAR_PAGE, pageCourante * PAR_PAGE);

  if (chargementStatut) {
    return (
      <div className="mt-4" data-testid="mes-attestations-chargement-statut">
        <LoadingBarWithStyles />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <h1 className="text-lg sm:text-xl font-bold text-slate-800">{t('mesAttestations.titre')}</h1>

      {/* Rien à proposer, jamais un bouton pour un type inconnu (ni les
          deux) : absence de contrat de travail comme de stage. */}
      {!statutEmploi ? (
        <p data-testid="mes-attestations-sans-statut" className="text-sm text-gray-500">
          {t('mesAttestations.sansStatut')}
        </p>
      ) : (
        <div
          className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100"
          data-testid="mes-attestations"
        >
          <div className="flex items-center justify-between gap-3 mb-3.5 pb-2.5 border-b border-slate-100">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center mr-3 bg-indigo-500/10 text-indigo-600">
                <FaFileAlt className="w-4 h-4" />
              </div>
              <h2 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
                {t('mesAttestations.listeTitre')}
              </h2>
            </div>
            <button
              type="button"
              data-testid="mes-attestations-demander"
              onClick={demander}
              disabled={enCours || dejaEnAttente}
              title={dejaEnAttente ? t('mesAttestations.dejaEnAttente') : undefined}
              className="text-sm font-medium text-white bg-[--color-primary] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg px-3 py-1.5 shrink-0"
            >
              {t(`mesAttestations.demander.${statutEmploi}`)}
            </button>
          </div>

          {erreur && (
            <p
              data-testid="mes-attestations-erreur"
              className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {t('mesAttestations.erreur')}
            </p>
          )}

          {chargement ? (
            <LoadingBarWithStyles />
          ) : demandes.length === 0 ? (
            <p data-testid="mes-attestations-vide" className="text-sm text-gray-500">
              {t('mesAttestations.vide')}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {affichees.map((demande) => {
                const typeAttestation = demande.contenu?.type_attestation;
                const dateRemise = demande.contenu?.date_remise;
                return (
                  <li
                    key={demande.id}
                    data-testid={`mes-attestations-ligne-${demande.id}`}
                    className="flex flex-col gap-1 py-2 first:pt-0 last:pb-0 text-sm"
                  >
                    {/* Le type distingue deux attestations de statuts
                        différents dans la même liste — sans lui, une
                        attestation de travail et une de stage étaient
                        indiscernables l'une de l'autre (feedback du
                        2026-09-25). */}
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                      <span
                        data-testid={`mes-attestations-type-${demande.id}`}
                        className="font-medium text-slate-700"
                      >
                        {t(`mesAttestations.types.${typeAttestation}`, { defaultValue: typeAttestation })}
                      </span>
                      <span
                        className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
                          COULEUR_ETAT[demande.etat] || COULEUR_ETAT_DEFAUT
                        }`}
                      >
                        {t(`mesAttestations.etats.${demande.etat}`, { defaultValue: demande.etat })}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span data-testid={`mes-attestations-date-demande-${demande.id}`}>
                        {t('mesAttestations.demandeeLe', { date: formaterJour(demande.date_demande, locale) })}
                      </span>
                      {/* La date de remise n'existe qu'une fois le geste RH
                          fait : absente tant que l'état n'est pas « remise »
                          (le backend ne l'envoie que dans ce cas). */}
                      {dateRemise && (
                        <span data-testid={`mes-attestations-date-remise-${demande.id}`}>
                          {t('mesAttestations.remiseLe', { date: formaterJour(dateRemise, locale) })}
                        </span>
                      )}
                    </div>
                    {/* Remise en main propre : plus de téléchargement, juste
                        l'invite à passer la récupérer une fois disponible. */}
                    {demande.etat === 'validee' && (
                      <span data-testid={`mes-attestations-disponible-${demande.id}`} className="text-xs text-slate-500">
                        {t('mesAttestations.disponibleInfo')}
                      </span>
                    )}
                    {demande.motif_refus && (
                      <span className="text-xs text-red-700">{demande.motif_refus}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
              <span data-testid="mes-attestations-pagination">
                {t('mesAttestations.page', { page: pageCourante, total: pages })}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  data-testid="mes-attestations-page-precedente"
                  disabled={pageCourante === 1}
                  onClick={() => setPage(pageCourante - 1)}
                  className="rounded-lg border border-slate-300 px-2.5 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('mesAttestations.precedente')}
                </button>
                <button
                  type="button"
                  data-testid="mes-attestations-page-suivante"
                  disabled={pageCourante === pages}
                  onClick={() => setPage(pageCourante + 1)}
                  className="rounded-lg border border-slate-300 px-2.5 py-1 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('mesAttestations.suivante')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MesAttestations;
